import { environment } from '@environment';
import {
  APP_FEATURES_KEYS,
  AppFeatures,
  AppFeaturesKeys,
} from '@feature-flags';
import { GrowthBook } from '@growthbook/growthbook';

import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import type { IdTokenResult, User } from '../../ui/services/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  public isSuccess = false;
  public features = signal<AppFeatures>({} as AppFeatures);

  growthbook!: GrowthBook;
  router = inject(Router);

  public async init(user: User, tokenResult: IdTokenResult | undefined) {
    this.growthbook = new GrowthBook({
      apiHost: environment.growthBook.apiHost,
      clientKey: environment.growthBook.clientKey,
      enableDevMode: false,
      subscribeToChanges: true,
    });

    this.growthbook.setAttributes({
      id: user?.uid,
      email: user?.email,
      clientName: tokenResult?.claims['client_name'],
    });

    const growthInit = await this.growthbook.init({
      streaming: true,
      skipCache: true,
    });

    this.isSuccess = growthInit.success;

    if (this.isSuccess) {
      this.evaluateFlags();

      // Use setRenderer for UI frameworks - triggers on any feature or attribute change
      this.growthbook.setRenderer(() => {
        this.evaluateFlags();
      });
    }
  }

  private evaluateFlags(): void {
    const freshFeatures = {} as AppFeatures;
    for (const key of APP_FEATURES_KEYS) {
      freshFeatures[key] = this.growthbook.isOn(key);
    }
    this.features.set(freshFeatures);
    this.checkActiveFeatureFlag();
  }

  /**
   * Updates the clientName attribute used for feature flag targeting,
   * e.g. when viewing a record that belongs to a specific client.
   */
  public async updateClientName(clientName: string | undefined): Promise<void> {
    if (!this.growthbook) {
      return;
    }

    await this.growthbook.updateAttributes({ clientName });
  }

  public getClientName(): string | undefined {
    return this.growthbook?.getAttributes()?.['clientName'];
  }

  public isOn(
    feature: AppFeaturesKeys | AppFeaturesKeys[] | undefined
  ): boolean {
    if (!feature) {
      return true;
    }

    if (Array.isArray(feature)) {
      return feature.some(f => this.features()[f] ?? false);
    }

    return this.features()[feature] ?? false;
  }

  /**
   * Checks if the active route is actually an active feature flag,
   * otherwise clear the active feature flag from the local storage
   * And redirects to home route
   */
  public checkActiveFeatureFlag(): void {
    const feature = localStorage.getItem('activeFeatureFlag');
    if (feature) {
      const result = this.growthbook.isOn(feature);
      if (!result) {
        localStorage.removeItem('activeFeatureFlag');
        this.router.navigate(['/']);
      }
    }
  }

  public destroy(): void {
    this.growthbook.destroy();
  }
}
